import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
export function SecurityPage() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const auditPoints = ['Who requested it — the human', 'Who executed it — the AI agent', 'What was accessed — the MCP / data source', 'Why it was allowed — the policy decision'];
  const previewImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/kQceHkNH6iAqUxzNH5qYLk/Screenshot_2026-02-06_at_16.56.51.png',
    alt: 'MCPGuard policy enforcement dashboard showing Developer Code Assistance workflow with connected agents, servers, real-time metrics, and instance details',
    caption: 'Policy enforcement & gateway overview'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/ve8Xr8SGGw1sM88gKrambF/Screenshot_2026-02-06_at_16.56.13.png',
    alt: 'MCPGuard workflow audit view showing action detail, chain of custody, authorization decision, MCP interaction, and audit integrity',
    caption: 'Workflow audit & action detail'
  }];
  const goNext = useCallback(() => {
    setExpandedIndex(prev => prev !== null ? (prev + 1) % previewImages.length : null);
  }, [previewImages.length]);
  const goPrev = useCallback(() => {
    setExpandedIndex(prev => prev !== null ? (prev - 1 + previewImages.length) % previewImages.length : null);
  }, [previewImages.length]);
  // Keyboard: Escape to close, Arrow keys to navigate
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedIndex(null);
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    if (expandedIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [expandedIndex, goNext, goPrev]);
  return <div className="max-w-4xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-506">
      <div className="text-center mb-16" data-id="element-507">
        <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4" data-id="element-508">
          Security & Auditability
        </h1>
        <h2 className="text-2xl font-medium text-gray-700 mb-8" data-id="element-509">
          Every AI Action Has a Paper Trail
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto text-left mb-12" data-id="element-510">
          {auditPoints.map((point, index) => <div key={index} className="flex items-start" data-id="element-511">
              <div className="flex-shrink-0 mt-1" data-id="element-512">
                <Check className="h-5 w-5 text-[#4db8a4]" data-id="element-513" />
              </div>
              <p className="ml-3 text-lg text-gray-700" data-id="element-514">{point}</p>
            </div>)}
        </div>

        <p className="text-2xl font-semibold text-[#1e3a5f] border-t border-b border-gray-100 py-8 inline-block w-full max-w-2xl" data-id="element-515">
          No black boxes. No guesswork.
        </p>
      </div>

      <div className="space-y-6" data-id="element-516">
        <div className="text-center" data-id="element-517">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-518">
            Preview of MCPGuard's policy enforcement and audit visibility
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2" data-id="element-519">
          {previewImages.map((image, index) => <button key={index} onClick={() => setExpandedIndex(index)} className="group rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200 cursor-pointer text-left bg-white" data-id="element-520">
              <div className="overflow-hidden" data-id="element-521">
                <img src={image.src} alt={image.alt} className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-300" data-id="element-522" />
              </div>
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between" data-id="element-523">
                <p className="text-sm text-gray-600 font-medium" data-id="element-524">
                  {image.caption}
                </p>
                <span className="text-xs text-gray-400 group-hover:text-[#4db8a4] transition-colors" data-id="element-525">
                  Click to expand
                </span>
              </div>
            </button>)}
        </div>
      </div>

      {/* Lightbox Modal with Navigation */}
      {expandedIndex !== null && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 md:p-8" onClick={() => setExpandedIndex(null)} data-id="element-526">
          {/* Close button */}
          <button onClick={() => setExpandedIndex(null)} className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close preview" data-id="element-527">
            <X className="h-6 w-6 text-white" data-id="element-528" />
          </button>

          {/* Previous arrow */}
          <button onClick={e => {
        e.stopPropagation();
        goPrev();
      }} className="absolute left-4 md:left-8 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Previous image" data-id="element-529">
            <ChevronLeft className="h-6 w-6 text-white" data-id="element-530" />
          </button>

          {/* Image + caption */}
          <div className="flex flex-col items-center max-w-full max-h-[90vh]" onClick={e => e.stopPropagation()} data-id="element-531">
            <img src={previewImages[expandedIndex].src} alt={previewImages[expandedIndex].alt} className="max-w-full max-h-[82vh] rounded-lg shadow-2xl object-contain" data-id="element-532" />
            <div className="mt-3 flex items-center space-x-3" data-id="element-533">
              <p className="text-sm text-white/80 font-medium" data-id="element-534">
                {previewImages[expandedIndex].caption}
              </p>
              <span className="text-xs text-white/50" data-id="element-535">
                {expandedIndex + 1} / {previewImages.length}
              </span>
            </div>
          </div>

          {/* Next arrow */}
          <button onClick={e => {
        e.stopPropagation();
        goNext();
      }} className="absolute right-4 md:right-8 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Next image" data-id="element-536">
            <ChevronRight className="h-6 w-6 text-white" data-id="element-537" />
          </button>
        </div>}
    </div>;
}